<template>
  <BaseGridRow
      small
      :default-col-size="6"
  >
    <BaseGridCol :s="{ xs: 12, lg: 6 }">
      <InputField
          label="Vorname"
          type="text"
          v-model="firstName"
          v-model:error="errorFirstName"
      />
    </BaseGridCol>
    <BaseGridCol :s="{ xs: 12, lg: 6 }">
      <InputField
          label="Nachname"
          type="text"
          v-model="lastName"
          v-model:error="errorLastName"
      />
    </BaseGridCol>
    <BaseGridCol :s="{ xs: 12, lg: 6 }">
      <InputField
          label="Postleitzahl*"
          :only-numeric="true"
          :max-length="5"
          :min-length="5"
          :show-loader="isFetching"
          v-model="postcode"
          v-model:error="errorPostcode"
      />
    </BaseGridCol>
    <BaseGridCol :s="{ xs: 12, lg: 6 }">
      <InputField
          v-if="showEmptyCity"
          label="Stadt*"
          readonly
          disabled
      />
      <SelectField
          v-else-if="citiesOptions.length > 1"
          label="Stadt*"
          v-model="city"
          :options="citiesOptions"
          v-model:error="errorCity"
      />
      <InputField
          v-else
          label="Stadt*"
          readonly
          disabled
          :model-value="city"
          v-model:error="errorCity"
      />
    </BaseGridCol>
    <template v-if="showStreets">
      <BaseGridCol
          :s="12"
          :order="3"
      >
        <SelectField
            label="Straße*"
            v-model="street"
            :options="streetsOptions"
            :search="true"
            v-model:error="errorStreet"
        />
      </BaseGridCol>
      <BaseGridCol
          :s="{ xs: 12, lg: 6 }"
          :order="4"
      >
        <InputField
            label="Haus-Nr.*"
            type="text"
            v-model="houseNumber"
            v-model:error="errorHouseNumber"
        />
      </BaseGridCol>
      <BaseGridCol
          :s="{ xs: 12, lg: 6 }"
          :order="5"
      >
        <InputField
            label="Hausnummerzusatz"
            type="text"
            v-model="houseNumberAddon"
            v-model:error="errorHouseNumberAddon"
        />
      </BaseGridCol>
    </template>
  </BaseGridRow>
</template>

<script setup lang="ts">
import { EnetAPI } from '@/api';
import BaseGridCol from '@/components/BaseGridCol.vue';
import BaseGridRow from '@/components/BaseGridRow.vue';
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import CityInterface from '@/interfaces/CityInterface';
import StreetInterface from '@/interfaces/StreetInterface';
import { computed, watch, ref } from 'vue';

const firstName = defineModel('firstName');
const errorFirstName = defineModel('errorFirstName');
const lastName = defineModel('lastName');
const errorLastName = defineModel('errorLastName');
const postcode = defineModel('postcode');
const errorPostcode = defineModel('errorPostcode');
const city = defineModel('city');
const errorCity = defineModel('errorCity');
const street = defineModel('street');
const errorStreet = defineModel('errorStreet');
const houseNumber = defineModel('houseNumber');
const errorHouseNumber = defineModel('errorHouseNumber');
const houseNumberAddon = defineModel('houseNumberAddon');
const errorHouseNumberAddon = defineModel('errorHouseNumberAddon');

const selectableCities = ref<CityInterface[]>([]);
const selectableStreets = ref<StreetInterface[]>([]);
const postcodeLoaded = ref<string>('');
const isFetching = ref<boolean>(false);

const fetchCitiesAndStreets = ($postcode: string | number) => {
  if (`${$postcode}`.length === 5 && $postcode !== postcodeLoaded.value) {
    postcodeLoaded.value = $postcode as string;
    isFetching.value = true;

    EnetAPI.getCitiesAndStreets($postcode).then((data) => {
      selectableCities.value = data.cities;
      if (data.cities.length === 1) {
        city.value = data.cities[0].city;
      } else if (city.value) {
        const responseCity = (data.cities || []).find((resCity) => resCity.city === city.value);

        if (!responseCity) {
          city.value = undefined;
        }
      }
      selectableStreets.value = data.streets;
      if (street.value !== undefined) {
        const responseStreet = (data.streets || []).find((resStreet) => resStreet.street === street.value);

        if (!responseStreet) {
          street.value = undefined;
        }
      }
    }).catch(() => {
      selectableCities.value = [];
      selectableStreets.value = [];
      city.value = undefined;
      street.value = undefined;
    }).finally(() => {
      isFetching.value = false;
    });
  }
};

watch(() => postcode.value, (newPostcode: any) => {
  fetchCitiesAndStreets(newPostcode as string);
});

if (postcode.value) {
  fetchCitiesAndStreets(postcode.value as string);
}

const citiesOptions = computed(() => (
  [...selectableCities.value].map(($c) => ({
    label: $c.city,
    value: $c.city,
  }))));

const streetsOptions = computed(() => (
  [...selectableStreets.value].map(($s) => ({
    label: $s.street,
    value: $s.street,
  }))));

const showStreets = computed(() => (
  postcode.value
    && `${postcode.value}`.length === 5
    && streetsOptions.value.length
));

const showEmptyCity = computed(() => (
  `${postcode.value}`.length !== 5
    || citiesOptions.value.length === 0
));
</script>
